import { useState } from 'react';
import { get } from 'lodash';
import { Button, ButtonGroup } from '@lendi-ui/button';
import Modal from '@lendi-ui/modal';
import { RadioGroup } from '@lendi-ui/radio';
import { Body } from '@lendi-ui/typography';

import { FIELD_PATHS, VISIT_CONTACT_TYPE } from '@funnels/constants';
import { Asset, RelatedParty, RelatedPartyEntityIndex } from '@funnels/types';
import { getIndexFromId } from '@funnels/utils';

import { Link } from '../../style';

interface ContactCopierProps extends RelatedPartyEntityIndex {
  assets: Asset[];
  relatedParties: RelatedParty[];
  copyContact: (fromIndex: number) => void;
  size?: 'lg' | 'md' | 'sm' | 'xs' | 'xxs';
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export const ContactCopier = ({
  assets,
  relatedParties,
  relatedPartyIndex,
  copyContact,
  size = 'xs',
}: ContactCopierProps) => {
  const [selectedRelatedPartyIndex, setSelectedRelatedPartyIndex] = useState<string | null>(null);
  const [modalVisibility, setModalVisibility] = useState(false);

  return (
    <>
      <Link
        size={size}
        onClick={() => {
          setModalVisibility(!modalVisibility);
        }}
      >
        Copy details from another property
      </Link>
      <Modal
        role="dialog"
        isVisible={modalVisibility}
        size="lg"
        isHeaderFixed
        onHide={() => {
          setSelectedRelatedPartyIndex(null);
          setModalVisibility(!modalVisibility);
        }}
      >
        <Modal.Header title="Copy details from another property" />
        <Modal.Content>
          <Body size="md">Select from one of the following properties to copy the contact details</Body>
          <RadioGroup
            onChange={(e) => setSelectedRelatedPartyIndex(e.target.value)}
            selectedValue={selectedRelatedPartyIndex?.toString() || ''}
          >
            {relatedParties
              .map((_, index) => {
                // loop through related parties and if the related party is about contact for access,
                // and has at least a contact type, then create a radio in the group,
                // and get the address with the associated asset id
                const assetId = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_ASSET_ID(index));
                const contactType = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_TYPE(index));
                if (
                  !!assetId &&
                  Object.values(VISIT_CONTACT_TYPE).includes(contactType) &&
                  index !== relatedPartyIndex
                ) {
                  const assetIndex = getIndexFromId(assets, assetId);
                  const address: string = get({ assets }, FIELD_PATHS.ASSET_FORMATTED_ADDRESS(assetIndex));
                  return <RadioGroup.Radio key={assetId} label={address} value={index.toString()} />;
                }
                return null;
              })
              .filter(notEmpty)}
          </RadioGroup>
        </Modal.Content>
        <Modal.Footer>
          <ButtonGroup isFullWidth size="md">
            <Button
              variant="primary"
              isDisabled={!selectedRelatedPartyIndex}
              isFullWidth
              onClick={() => {
                copyContact(+selectedRelatedPartyIndex!);
                setModalVisibility(!modalVisibility);
              }}
            >
              Done
            </Button>
            <Button variant="secondary" isFullWidth onClick={() => setModalVisibility(!modalVisibility)}>
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  );
};
